import { createTripCompleter, errorCode, type SpentStore, type TripOutcome } from "@/components/github/logic"

// The parts of the Slack screens that are decisions rather than markup: what
// Slack handed back to the callback page, what each refusal is called, and
// where a finished trip goes. The trip itself is finished once, as GitHub's
// is (components/github/logic.ts).

export type SlackCallbackParams = { state: string; code?: string; error?: string }

/**
 * What `/slack/callback` was given, or null without a `state`. Read from the
 * raw query string, as GitHub's is, so that nothing is read as a number.
 */
export function parseSlackCallbackSearch(search: string): SlackCallbackParams | null {
  const query = new URLSearchParams(search)
  const state = query.get("state")
  if (!state) return null
  return {
    state,
    code: query.get("code") || undefined,
    error: query.get("error") || undefined,
  }
}

export type SlackCompletion = {
  workspaceSlug: string | null
  result: "connected" | "error"
  reason: string | null
}

/** Every reason the interface has a sentence for. `unknown` is the rest. */
export const SLACK_REASONS = [
  "state_invalid",
  "state_mismatch",
  "denied",
  "code_missing",
  "exchange_failed",
  "forbidden",
  "team_taken",
  "not_configured",
  "unknown",
] as const
export type SlackReason = (typeof SLACK_REASONS)[number]

export function slackReasonKey(reason: string | null | undefined): SlackReason {
  return SLACK_REASONS.find((known) => known === reason) ?? "unknown"
}

const THROWN_REASONS: Record<string, SlackReason> = {
  slackStateMismatch: "state_mismatch",
  slackNotConfigured: "not_configured",
}

export function slackCompletionFromError(failure: unknown): SlackCompletion {
  const code = errorCode(failure)
  return { workspaceSlug: null, result: "error", reason: (code && THROWN_REASONS[code]) || "unknown" }
}

export function createSlackCompleter(
  run: (params: SlackCallbackParams) => Promise<SlackCompletion>,
  store: SpentStore,
) {
  return createTripCompleter(run, store, "echoes_slack_trips", slackCompletionFromError)
}

/** What the integrations page is told in its address, after a trip to Slack. */
export type SlackOutcomeSearch = { slack?: SlackCompletion["result"]; slackReason?: SlackReason }

/** Where a finished trip sends the browser, and what it says there. */
export function slackDestination(
  outcome: TripOutcome<SlackCompletion>,
):
  | { to: "integrations"; workspaceSlug: string; search: SlackOutcomeSearch }
  | { to: "root"; reason: SlackReason | null } {
  if (outcome.kind === "replayed") {
    return outcome.workspaceSlug === null
      ? { to: "root", reason: "state_invalid" }
      : { to: "integrations", workspaceSlug: outcome.workspaceSlug, search: {} }
  }
  const { workspaceSlug, result, reason } = outcome.completion
  if (workspaceSlug === null) {
    return { to: "root", reason: result === "error" ? slackReasonKey(reason) : null }
  }
  return {
    to: "integrations",
    workspaceSlug,
    search:
      result === "error" ? { slack: result, slackReason: slackReasonKey(reason) } : { slack: result },
  }
}

/** The integrations page's Slack search, from whatever the address holds. */
export function parseSlackOutcomeSearch(search: Record<string, unknown>): SlackOutcomeSearch {
  if (search.slack === "connected") return { slack: "connected" }
  if (search.slack !== "error") return {}
  return {
    slack: "error",
    slackReason: slackReasonKey(typeof search.slackReason === "string" ? search.slackReason : null),
  }
}
